/**
 * vpv-simple-subs.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Dawn-model product form + VPV variant gallery swap.
 *
 * Key architecture decisions following Dawn's product-form.js:
 *
 * 1. The <form> is rendered by Liquid's {% form 'product', product %} tag.
 *    This gives us action="/cart/add", method="post", and the authenticity_token.
 *    Recharge discovers its host form by querying form[action*="cart/add"] —
 *    the Liquid tag guarantees that attribute is present.
 *
 * 2. ATC reads selling_plan from the form, builds explicit JSON, POSTs to /cart/add.js
 *    Recharge v2 intercepts multipart FormData, so we bypass it with clean JSON.
 *    We then call formData.set('id', variantId) to override the variant input.
 *
 * 3. Content-Type: application/json — explicit clean payload, Recharge cannot intercept.
 *    with the correct boundary automatically. This is the Dawn pattern.
 *
 * Custom elements:
 *   <vpv-subs-gallery>  gallery carousel, swaps on variant change
 *   <vpv-subs-form>     wraps the Liquid-rendered form, handles variant UI + ATC
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Utilities ────────────────────────────────────────────────────────────────

  function getProductData(sectionId) {
    const el = document.getElementById('vpv-subs-data-' + sectionId);
    if (!el) { console.error('[vpv-subs] Missing data blob for section', sectionId); return null; }
    try { return JSON.parse(el.textContent); }
    catch (e) { console.error('[vpv-subs] JSON parse error:', e); return null; }
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency', currency, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (_) {
      return '$' + (whole ? (cents / 100).toFixed(0) : (cents / 100).toFixed(2));
    }
  }

  const GALLERY_EVENT = (id) => 'vpv-subs:variant-change:' + id;


  // ═══════════════════════════════════════════════════════════════════════════
  //  <vpv-subs-gallery>
  // ═══════════════════════════════════════════════════════════════════════════

  class VpvSubsGallery extends HTMLElement {
    constructor() {
      super();
      this._index   = 0;
      this._media   = [];
      this._data    = null;
      this._cleanup = [];
    }

    connectedCallback() {
      this._sectionId = this.dataset.section;
      this._data = getProductData(this._sectionId);
      if (!this._data) return;

      this._slidesEl   = this.querySelector('.vpv-subs__slides');
      this._dotsEl     = this.querySelector('.vpv-subs__dots');
      this._infoGridEl = this.querySelector('[data-vpv-info-grid]');
      this._prevBtn    = this.querySelector('.vpv-subs__arrow--prev');
      this._nextBtn    = this.querySelector('.vpv-subs__arrow--next');

      this._prevBtn?.addEventListener('click', () => this._goTo(this._index - 1));
      this._nextBtn?.addEventListener('click', () => this._goTo(this._index + 1));
      this._initSwipe();
      this._initLightbox();
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  this._goTo(this._index - 1);
        if (e.key === 'ArrowRight') this._goTo(this._index + 1);
      });

      const handler = (e) => this._onVariantChange(e.detail.variant);
      document.addEventListener(GALLERY_EVENT(this._sectionId), handler);
      this._cleanup.push(() => document.removeEventListener(GALLERY_EVENT(this._sectionId), handler));

      this._loadInitialMedia();
    }

    disconnectedCallback() { this._cleanup.forEach(fn => fn()); }

    // ── Media loading ─────────────────────────────────────────────────────────

    _loadInitialMedia() {
      const d = this._data;
      if (d.has_only_default_variant) {
        this._setMedia(d.native_media || []);
        this._renderInfoGrid(d.product_info_grid || []);
        return;
      }
      const id      = parseInt(this.dataset.currentVariant, 10);
      const variant = d.variants.find(v => v.id === id) || d.variants[0];
      if (!variant) return;
      this._setMedia(this._resolveMedia(variant));
      this._renderInfoGrid(this._resolveInfoGrid(variant));
    }

    _setMedia(arr) {
      this._media = Array.isArray(arr) ? arr : [];
      this._index = 0;
      this._renderSlides();
      this._renderDots();
      this._updateArrows();
    }

    // ── Slide rendering ───────────────────────────────────────────────────────

    _renderSlides() {
      if (!this._slidesEl) return;
      this._slidesEl.innerHTML = this._media.map((m, i) => this._buildSlide(m, i)).join('');
      // innerHTML doesn't set .muted DOM property — must set it explicitly
      this._slidesEl.querySelectorAll('video').forEach(v => { v.muted = true; });
      this._activateSlide(0, false);
    }

    _buildSlide(m, i) {
      const ac = i === 0 ? ' is-active' : '';

      if (m.type === 'video') {
        return `<div class="vpv-simple__slide${ac}" data-index="${i}">
          <video class="vpv-subs__media vpv-subs__media--video"
            src="${this._esc(m.url)}" poster="${this._esc(m.preview || '')}"
            playsinline autoplay loop muted preload="auto"
            aria-label="${this._esc(m.alt || 'Product video')}"></video>
        </div>`;
      }

      if (m.type === 'external_video') {
        const host = m.host || 'youtube';
        const src  = host === 'vimeo'
          ? `https://player.vimeo.com/video/${m.url}?autoplay=0&title=0&byline=0`
          : `https://www.youtube.com/embed/${m.url}?autoplay=0&rel=0`;
        return `<div class="vpv-simple__slide vpv-simple__slide--video${ac}" data-index="${i}">
          <div class="vpv-subs__media vpv-subs__media--iframe">
            <iframe src="${src}" frameborder="0" allow="autoplay; encrypted-media"
              allowfullscreen loading="lazy"
              title="${this._esc(m.alt || 'Product video')}"></iframe>
          </div>
        </div>`;
      }

      // Image (default)
      return `<div class="vpv-simple__slide${ac}" data-index="${i}">
        <button class="vpv-subs__lightbox-trigger" type="button"
          data-lightbox-index="${i}"
          aria-label="View image ${i + 1} fullscreen"
        ><img class="vpv-subs__media vpv-subs__media--image"
            src="${this._esc(m.url)}" alt="${this._esc(m.alt || '')}"
            loading="${i === 0 ? 'eager' : 'lazy'}" decoding="async" draggable="false"
          ></button>
      </div>`;
    }

    _renderDots() {
      if (!this._dotsEl) return;
      if (this._media.length <= 1) { this._dotsEl.innerHTML = ''; return; }
      this._dotsEl.innerHTML = this._media.map((_, i) =>
        `<button class="vpv-subs__dot${i === 0 ? ' is-active' : ''}"
          data-index="${i}"
          aria-label="Go to image ${i + 1}"></button>`
      ).join('');
      this._dotsEl.querySelectorAll('.vpv-subs__dot').forEach(btn =>
        btn.addEventListener('click', () => this._goTo(parseInt(btn.dataset.index, 10)))
      );
    }

    // ── Navigation ────────────────────────────────────────────────────────────

    _goTo(index) {
      const n = this._media.length;
      if (!n) return;
      this._index = ((index % n) + n) % n;
      this._activateSlide(this._index, true);
      this._updateArrows();
    }

    _activateSlide(index, animate) {
      if (!animate && this._slidesEl) {
        this._slidesEl.querySelectorAll('.vpv-simple__slide').forEach(s => { s.style.transition = 'none'; });
        void this._slidesEl.offsetWidth; // force reflow
      }
      this._slidesEl?.querySelectorAll('.vpv-simple__slide').forEach((s, i) => {
        const active = i === index;
        s.classList.toggle('is-active', active);
        const vid = s.querySelector('video');
        if (vid) {
          if (active) { vid.muted = true; requestAnimationFrame(() => vid.play().catch(() => {})); }
          else vid.pause();
        }
        if (!animate) s.style.transition = '';
      });
      this._dotsEl?.querySelectorAll('.vpv-subs__dot').forEach((t, i) => {
        const active = i === index;
        t.classList.toggle('is-active', active);
      });
    }

    _updateArrows() {
      const single = this._media.length <= 1;
      if (this._prevBtn) this._prevBtn.disabled = single;
      if (this._nextBtn) this._nextBtn.disabled = single;
      this.querySelector('.vpv-simple__gallery-main')?.classList.toggle('is-single-slide', single);
    }

    // ── Lightbox ──────────────────────────────────────────────────────────────

    _initLightbox() {
      const open = (e) => {
        const t = e.target.closest('.vpv-subs__lightbox-trigger');
        if (t) this._triggerLightbox(parseInt(t.dataset.lightboxIndex, 10));
      };
      this._slidesEl?.addEventListener('click', open);
      this._slidesEl?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const t = e.target.closest('.vpv-subs__lightbox-trigger');
        if (!t) return;
        e.preventDefault();
        this._triggerLightbox(parseInt(t.dataset.lightboxIndex, 10));
      });
    }

    async _triggerLightbox(mediaIndex) {
      const items = [];
      let lightboxIndex = 0;
      this._media.forEach((m, i) => {
        if (i === mediaIndex) lightboxIndex = items.length;
        if (m.type === 'image') {
          items.push({ src: m.url, msrc: m.preview || m.url, alt: m.alt || '', width: 1200, height: 1200 });
        } else if (m.type === 'video') {
          items.push({ html: `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%"><video src="${this._esc(m.url)}" poster="${this._esc(m.preview||'')}" controls playsinline loop preload="auto" style="max-width:100%;max-height:80vh;display:block;"></video></div>` });
        } else if (m.type === 'external_video') {
          const src = (m.host === 'vimeo')
            ? `https://player.vimeo.com/video/${m.url}?autoplay=1`
            : `https://www.youtube.com/embed/${m.url}?autoplay=1&rel=0`;
          items.push({ html: `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%"><iframe src="${src}" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen style="width:min(100%,900px);aspect-ratio:16/9"></iframe></div>` });
        }
      });
      if (!items.length) return;
      const PSwipe = await this._loadPswp();
      if (!PSwipe) return;
      const activeVid = this._slidesEl?.querySelector('.vpv-simple__slide.is-active video');
      if (activeVid) activeVid.pause();
      const pswp = new PSwipe({
        dataSource: items, index: lightboxIndex, bgOpacity: 0.95,
        zoom: true, maxZoomLevel: 4, initialZoomLevel: 'fit', secondaryZoomLevel: 2,
        wheelToZoom: true, pinchToClose: true, closeOnVerticalDrag: true,
        padding: { top: 20, bottom: 20, left: 20, right: 20 },
      });
      pswp.on('destroy', () => { if (activeVid) { activeVid.muted = true; activeVid.play().catch(() => {}); } });
      pswp.init();
    }

    async _loadPswp() {
      if (!document.getElementById('vpv-pswp-css')) {
        document.head.appendChild(Object.assign(document.createElement('link'), {
          id: 'vpv-pswp-css', rel: 'stylesheet',
          href: 'https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe.css',
        }));
      }
      if (!window.__vpvPswpPromise) {
        window.__vpvPswpPromise = import('https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe.esm.min.js')
          .then(m => m.default)
          .catch(err => { console.error('[vpv-subs] PhotoSwipe failed:', err); window.__vpvPswpPromise = null; return null; });
      }
      return window.__vpvPswpPromise;
    }

    // ── Touch swipe ───────────────────────────────────────────────────────────

    _initSwipe() {
      let sx = 0, sy = 0;
      this._slidesEl?.addEventListener('touchstart', (e) => {
        sx = e.touches[0].clientX; sy = e.touches[0].clientY;
      }, { passive: true });
      this._slidesEl?.addEventListener('touchend', (e) => {
        const dx = sx - e.changedTouches[0].clientX;
        const dy = sy - e.changedTouches[0].clientY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40)
          dx > 0 ? this._goTo(this._index + 1) : this._goTo(this._index - 1);
      }, { passive: true });
    }

    // ── Variant change ────────────────────────────────────────────────────────

    _onVariantChange(variant) {
      if (!variant || this._data.has_only_default_variant) return;
      this._setMedia(this._resolveMedia(variant));
      this._renderInfoGrid(this._resolveInfoGrid(variant));
    }

    _resolveMedia(variant) {
      if (variant?.gallery?.length) return variant.gallery;
      if (variant?.variant_image?.url) return [variant.variant_image];
      return this._data.native_media || [];
    }

    _resolveInfoGrid(variant) {
      if (variant?.info_grid?.length) return variant.info_grid;
      return this._data.product_info_grid || [];
    }

    _renderInfoGrid(items) {
      if (!this._infoGridEl) return;
      if (!items?.length) { this._infoGridEl.innerHTML = ''; return; }
      this._infoGridEl.innerHTML = items.slice(0, 2).map(item =>
        item.type === 'video'
          ? `<div class="meta_img meta_video"><video src="${this._esc(item.url)}" autoplay muted loop playsinline style="display:block;object-fit:cover;width:100%;height:100%;"></video></div>`
          : `<div class="meta_img"><img src="${this._esc(item.url)}" alt="${this._esc(item.alt||'')}" width="100%" height="auto" loading="lazy" decoding="async"></div>`
      ).join('');
    }

    _esc(str) {
      return String(str)
        .replace(/&/g,'&amp;').replace(/"/g,'&quot;')
        .replace(/</g,'&lt;').replace(/>/g,'&gt;');
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  //  <vpv-subs-form>
  // ═══════════════════════════════════════════════════════════════════════════

  class VpvSubsForm extends HTMLElement {
    connectedCallback() {
      this._sectionId = this.dataset.section;
      this._data = getProductData(this._sectionId);
      if (!this._data) return;

      // ── The native Shopify form rendered by {% form 'product', product %} ──
      // action="/cart/add" is what Recharge queries to locate its host form.
      this.form = this.querySelector('form[action*="/cart/add"]');
      if (!this.form) {
        console.error('[vpv-subs] form[action*="/cart/add"] not found — check Liquid');
        return;
      }

      // The hidden [name="id"] input — Shopify and Recharge both read this
      this.variantInput = this.form.querySelector('[name="id"]');
      if (!this.variantInput) {
        console.error('[vpv-subs] [name="id"] input not found in form');
        return;
      }

      // UI refs
      this.submitBtn     = this.querySelector('[data-vpv-atc]');
      this.submitBtnText = this.querySelector('[data-vpv-atc-text]');
      this._priceEls     = this.querySelectorAll('[data-vpv-price]');
      this._compareEls   = this.querySelectorAll('[data-vpv-compare-price]');
      this._saleBadges   = this.querySelectorAll('[data-vpv-sale-badge]');
      this._qtyInput     = this.querySelector('[data-vpv-qty]');

      // ── Resolve current variant ───────────────────────────────────────────
      const initId = parseInt(this.dataset.currentVariant, 10);
      this._currentVariant = this._data.variants.find(v => v.id === initId)
        || this._data.variants.find(v => v.available)
        || this._data.variants[0];

      if (!this._currentVariant) {
        console.error('[vpv-subs] No variants in product data');
        return;
      }

      this._selectedOptions = [
        this._currentVariant.option1,
        this._currentVariant.option2,
        this._currentVariant.option3,
      ].filter(Boolean);

      // ── Option buttons ────────────────────────────────────────────────────
      this.querySelectorAll('[data-option-index]').forEach(btn =>
        btn.addEventListener('click', () => this._onOptionClick(btn))
      );

      // ── Quantity stepper ──────────────────────────────────────────────────
      this.querySelector('[data-vpv-qty-dec]')?.addEventListener('click', () => {
        if (this._qtyInput)
          this._qtyInput.value = Math.max(1, parseInt(this._qtyInput.value || 1) - 1);
      });
      this.querySelector('[data-vpv-qty-inc]')?.addEventListener('click', () => {
        if (this._qtyInput)
          this._qtyInput.value = parseInt(this._qtyInput.value || 1) + 1;
      });

      // ── ATC button click handler ─────────────────────────────────────────
      // We use a DIRECT CLICK listener on the button rather than form submit.
      //
      // The page has nested <form action="/cart/add"> tags inside our outer
      // form (from the complementary products snippet). In Chrome, nested
      // forms cause the outer form to be terminated early in the DOM, making
      // form submit events unreliable. UpCart also hooks form submit events
      // and may read the wrong [name="id"] before our handler fires.
      //
      // A direct click listener with e.stopPropagation() cuts through all of
      // this — we call _addToCart() directly with our JS-controlled state,
      // never relying on the native form submission path.
      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
          this._addToCart();
        });
      }

      // Block any residual native form submit (e.g. Enter key in an input)
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
      });

      // ── Recharge price display sync ───────────────────────────────────────
      this._watchSellingPlan();
    }

    // ── Option selection ──────────────────────────────────────────────────────

    _onOptionClick(btn) {
      const idx   = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.value;
      this._selectedOptions[idx] = value;

      btn.closest('[data-vpv-group]')
        ?.querySelectorAll('[data-option-index]')
        .forEach(b => {
          const sel = b.dataset.value === value;
          b.classList.toggle('is-selected', sel);
          b.setAttribute('aria-pressed', String(sel));
        });

      const label = this.querySelector(`[data-vpv-selected-label="${idx}"]`);
      if (label) label.textContent = value;

      const variant = this._findVariant();
      if (variant) {
        this._currentVariant = variant;
        this._updateVariantUI(variant);
      } else {
        this._setButtonState(false, 'Unavailable');
      }
    }

    _findVariant() {
      return this._data.variants.find(v => {
        if (v.enabled === false) return false;
        const opts = [v.option1, v.option2, v.option3].filter(o => o != null);
        return opts.every((o, i) => o === this._selectedOptions[i]);
      }) || null;
    }

    // ── Variant UI update ─────────────────────────────────────────────────────

    _updateVariantUI(variant) {
      // 1. CRITICAL: update [name="id"] — this is what ends up in FormData
      this.variantInput.value = String(variant.id);

      // 2. Fire change on the variant input so Recharge re-renders its widget
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      // 3. Update Recharge widget attribute → triggers attributeChangedCallback
      //    which re-renders plan options for the new variant
      this.querySelector('recharge-subscription-widget')
        ?.setAttribute('default-variant-id', String(variant.id));

      // 4. URL (no reload)
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', variant.id);
        history.replaceState({}, '', u.toString());
      } catch (_) {}

      // 5. Price — show variant price first; Recharge sync overrides if plan active
      this._updatePriceDisplay(variant);
      setTimeout(() => this._syncPriceFromRecharge(), 100);

      // 6. Preorder message
      const pm = this.querySelector('[data-vpv-preorder-message]');
      const pt = this.querySelector('[data-vpv-preorder-text]');
      if (pm && pt) {
        const msg = variant.preorder_message || '';
        pt.textContent = msg;
        pm.classList.toggle('preorder-hide', !msg);
      }

      // 7. Button
      this._setButtonState(variant.available,
        variant.available
          ? (this.dataset.atcText || 'Add to Cart')
          : (this.dataset.soldoutText || 'Sold Out')
      );

      // 8. Tell gallery to swap
      document.dispatchEvent(new CustomEvent(GALLERY_EVENT(this._sectionId), {
        bubbles: true, detail: { variant },
      }));
    }

    // ── Price helpers ─────────────────────────────────────────────────────────

    _updatePriceDisplay(variant) {
      this._priceEls.forEach(el => { el.textContent = formatMoney(variant.price); });
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      this._compareEls.forEach(el => {
        el.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        el.toggleAttribute('hidden', !onSale);
      });
      this._saleBadges.forEach(el => el.toggleAttribute('hidden', !onSale));
    }

    _setButtonState(available, text) {
      if (!this.submitBtn) return;
      if (available) {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-sold-out');
      } else {
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('is-sold-out');
      }
      if (this.submitBtnText && text) this.submitBtnText.textContent = text;
    }

    // ── Recharge selling plan price sync ──────────────────────────────────────

    /**
     * Recharge sets [name="selling_plan"] programmatically — this does NOT fire
     * a native `change` event. The widget's own custom events are the reliable
     * signal. We also watch `change`/`input` on the form as a fallback.
     */
    _watchSellingPlan() {
      const rcEventNames = [
        'rc:updateSellingPlan',
        'rc:subscriptionSelected',
        'rc:oneTimePurchaseSelected',
        'rc:planChanged',
        'rc:cartAdd',          // Recharge v2 sometimes fires this on selection
      ];

      // Recharge v2 dispatches events at document level (not on the widget
      // element). Widget-level listeners are unreliable because the widget uses
      // Shadow DOM and events dispatched inside it don't bubble out unless
      // composed:true. We listen at document for all RC events.
      const handler = () => this._syncPriceFromRecharge();
      rcEventNames.forEach(name => document.addEventListener(name, handler));

      // Also attach to the widget element directly (belt + suspenders)
      const attachToWidget = () => {
        const w = this.querySelector('recharge-subscription-widget');
        if (w && !w._vpvPriceAttached) {
          w._vpvPriceAttached = true;
          rcEventNames.forEach(name => w.addEventListener(name, handler));
        }
      };

      // Try now; observe for late Recharge render
      attachToWidget();
      if (!this.querySelector('recharge-subscription-widget')) {
        const obs = new MutationObserver(() => {
          if (this.querySelector('recharge-subscription-widget')) {
            attachToWidget();
            obs.disconnect();
          }
        });
        obs.observe(this, { childList: true, subtree: true });
      }

      // Fallback: form change/input (fires if Recharge writes an actual input event)
      this.form?.addEventListener('change', (e) => {
        if (e.target.name === 'selling_plan') this._syncPriceFromRecharge();
      });
      this.form?.addEventListener('input', (e) => {
        if (e.target.name === 'selling_plan') this._syncPriceFromRecharge();
      });

      // Fallback: poll the selling_plan value every 300ms for up to 5s after
      // load, then stop. This catches cases where Recharge writes the input
      // value programmatically without firing any events.
      let _lastPlanVal = null;
      let _pollCount = 0;
      const _poll = () => {
        const sp = this.form?.querySelector('[name="selling_plan"]');
        const val = sp?.value ?? '';
        if (val !== _lastPlanVal) { _lastPlanVal = val; this._syncPriceFromRecharge(); }
        if (++_pollCount < 80) setTimeout(_poll, 300); // 80 * 300ms = 24s
      };
      if (document.readyState === 'complete') {
        _poll();
      } else {
        window.addEventListener('load', _poll, { once: true });
      }
    }

    _syncPriceFromRecharge() {
      const spInput = this.form.querySelector('[name="selling_plan"]');
      const planId  = spInput?.value ? parseInt(spInput.value, 10) : null;
      const vid     = this._currentVariant?.id;
      if (!vid) return;
      const prices  = window.SubscriptionWidgetConfig?.formattedPrices?.[vid];

      if (planId && prices?.sellingPlans?.[planId]) {
        // Subscription selected — show discounted price
        this._priceEls.forEach(el => { el.innerHTML = prices.sellingPlans[planId].price; });
        this._compareEls.forEach(el => { el.innerHTML = prices.unit; el.removeAttribute('hidden'); });
        this._saleBadges.forEach(el => el.removeAttribute('hidden'));
      } else if (this._currentVariant) {
        // One-time / no plan — show normal variant prices
        this._updatePriceDisplay(this._currentVariant);
      }
    }

    // ── Add to cart — Dawn pattern ─────────────────────────────────────────────

    async _addToCart() {
      if (!this._currentVariant?.available) return;

      this.submitBtn?.setAttribute('aria-disabled', 'true');
      this.submitBtn?.classList.add('loading');

      try {
        /**
         * The "Required parameter missing or invalid: items" error means
         * Recharge is intercepting multipart FormData submissions and routing
         * them to their own API (which expects an `items` array format).
         *
         * Solution: bypass all form encoding ambiguity and POST explicit JSON
         * directly to Shopify's /cart/add.js. We read only the three fields
         * we actually need — variant ID, quantity, and selling_plan (if set).
         * Everything else Recharge injects into the form DOM is irrelevant to
         * the Shopify cart add call.
         *
         * This is the most defensive approach: we control exactly what gets
         * sent, the endpoint is always Shopify's, and the payload is clean.
         */
        const qty = this._qtyInput
          ? Math.max(1, parseInt(this._qtyInput.value) || 1)
          : 1;

        // Read selling_plan from whatever Recharge has injected into the form
        const spInput   = this.form?.querySelector('[name="selling_plan"]');
        const sellingPlan = spInput?.value ? parseInt(spInput.value, 10) : null;

        // Build clean item payload
        const item = {
          id:       this._currentVariant.id,
          quantity: qty,
        };
        // Only include selling_plan when a plan is actually selected —
        // Shopify returns a 422 if selling_plan is present but null/0/empty
        if (sellingPlan) item.selling_plan = sellingPlan;

        const root = window.Shopify?.routes?.root || '/';
        const res  = await fetch(root + 'cart/add.js', {
          method:  'POST',
          headers: {
            'Content-Type':     'application/json',
            'Accept':           'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(item),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'HTTP ' + res.status);
        }

        await this._refreshCartCount();
        this._openCartDrawer();

      } catch (err) {
        console.error('[vpv-subs] ATC error:', err);
        if (this.submitBtnText) {
          const prev = this.submitBtnText.textContent;
          this.submitBtnText.textContent = 'Error — Try Again';
          setTimeout(() => { if (this.submitBtnText) this.submitBtnText.textContent = prev; }, 2500);
        }
      } finally {
        this.submitBtn?.classList.remove('loading');
        this.submitBtn?.removeAttribute('aria-disabled');
      }
    }

    // ── Cart helpers ──────────────────────────────────────────────────────────

    async _refreshCartCount() {
      try {
        const cart  = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
        const count = cart.item_count;
        document.querySelectorAll(
          '.cart-count,.cart-count-bubble,.header__cart-count,[data-cart-count],[data-cart-item-count],#cart-count,.js-cart-count,.cart__count'
        ).forEach(el => {
          el.textContent = count;
          el.setAttribute('data-count', count);
          if (count > 0) el.removeAttribute('hidden');
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      } catch (_) {}
    }

    _openCartDrawer() {
      ['cart:open','cart:toggle','cart-drawer:open','CartDrawer:open','drawer:open','ajaxCart.afterCartLoad']
        .forEach(n => document.dispatchEvent(new CustomEvent(n, { bubbles: true, cancelable: true })));

      for (const sel of ['#cart-drawer','#CartDrawer','[data-cart-drawer]','[data-drawer="cart"]','.cart-drawer','.js-cart-drawer','.ajax-cart']) {
        const el = document.querySelector(sel);
        if (el) { el.classList.add('is-open','active','open'); el.removeAttribute('hidden'); el.setAttribute('aria-hidden','false'); break; }
      }

      const sec   = document.querySelector('[id^="shopify-section-"][id*="cart"]');
      const secId = sec?.id?.replace('shopify-section-', '');
      if (secId) {
        fetch('/?sections=' + secId, { headers: { Accept: 'application/json' } })
          .then(r => r.json())
          .then(sections => {
            const html  = sections[secId];
            if (!html || !sec) return;
            const inner = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-' + secId);
            if (inner) sec.innerHTML = inner.innerHTML;
          }).catch(() => {});
      }
    }
  }

  // ── Register ──────────────────────────────────────────────────────────────────

  if (!customElements.get('vpv-subs-gallery')) customElements.define('vpv-subs-gallery', VpvSubsGallery);
  if (!customElements.get('vpv-subs-form'))    customElements.define('vpv-subs-form',    VpvSubsForm);

})();
